import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { CONFIG_BASE_URL } from '../../core/constants';
import {
  Box,
  Flex,
  Text,
  Spinner,
  Input,
  HStack,
  VStack,
  useDisclosure,
  Button,
  CloseButton,
  Dialog,
  Portal
} from '@chakra-ui/react';
import EventDialog from './EventDialog';

export default function AdminAllEvents() {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState('');
  const [editorValue, setEditorValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState(null);
  const {
    open: isDialogOpen,
    onOpen: dialogOnOpen,
    onClose: dialogOnClose
  } = useDisclosure();
  const [dialogMode, setDialogMode] = useState('create');
  const {
    open: deleteIsOpen,
    onOpen: deleteOnOpen,
    onClose: deleteOnClose
  } = useDisclosure();

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    setLoading(true);
    try {
      const res = await axios.get(`${CONFIG_BASE_URL}/events.json`, {
        headers: { Accept: 'application/vnd.github.v3.raw' }
      });
      setEvents(res.data || {});
      setMessage(null);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Failed to load events' });
      setEvents({});
    } finally {
      setLoading(false);
    }
  }

  function openEdit(key) {
    setSelectedKey(key);
    setEditorValue(JSON.stringify(events[key], null, 2));
    setDialogMode('edit');
    dialogOnOpen();
  }

  function openCreate() {
    setSelectedKey('');
    setEditorValue('{\n  \n}');
    setDialogMode('create');
    dialogOnOpen();
  }

  async function handleDialogSave(id, content) {
    if (dialogMode === 'create') {
      if (!id) return setMessage({ type: 'warning', text: 'Provide an id' });
      if (events[id])
        return setMessage({ type: 'warning', text: 'Event id already exists' });
    }

    try {
      const parsed = JSON.parse(content);
      const updated = { ...events, [id]: parsed };
      await commitEvents(updated);
      setMessage({
        type: 'success',
        text: `Event ${dialogMode === 'create' ? 'created' : 'updated'}`
      });
      dialogOnClose();
      loadEvents();
    } catch (e) {
      setMessage({ type: 'error', text: 'Invalid JSON: ' + e.message });
    }
  }

  function openDelete(key) {
    setSelectedKey(key);
    deleteOnOpen();
  }

  async function deleteEvent() {
    try {
      const updated = { ...events };
      delete updated[selectedKey];
      await commitEvents(updated);
      setMessage({ type: 'success', text: 'Event deleted' });
      deleteOnClose();
      loadEvents();
    } catch (e) {
      setMessage({ type: 'error', text: 'Delete failed: ' + e.message });
    }
  }

  async function commitEvents(obj) {
    setIsSaving(true);
    try {
      await axios.post(
        '/api/github/update-config',
        {
          type: 'event',
          content: JSON.stringify(obj, null, 2)
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
    } finally {
      setIsSaving(false);
    }
  }

  if (loading)
    return (
      <Box p={4}>
        <Spinner />
      </Box>
    );

  return (
    <Box maxW="1100px" mx="auto">
      <Box mb={4}>
        <Button as={RouterLink} to="/admin/manager" variant="outline">
          Manage All Events json
        </Button>
      </Box>
      <Text as="h2" fontSize="xl" fontWeight="bold" mb={4}>
        Events (admin)
      </Text>

      <Flex mb={6}>
        <Input
          placeholder="Search events by title or id"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          width="280px"
          borderRadius="full"
          mr={4}
        />
        <Button colorPalette="orange" onClick={openCreate}>
          New Event
        </Button>
      </Flex>

      {message && (
        <Box
          mb={4}
          p={3}
          borderRadius="md"
          bg={
            message.type === 'error'
              ? 'red.100'
              : message.type === 'success'
                ? 'blue.100'
                : 'yellow.100'
          }
          color={
            message.type === 'error'
              ? 'red.800'
              : message.type === 'success'
                ? 'blue.800'
                : 'yellow.800'
          }
        >
          {message.text}
        </Box>
      )}

      {!events || Object.keys(events).length === 0 ? (
        <Box p={4} borderRadius="md" borderWidth={1} borderColor="gray.200">
          No events found
        </Box>
      ) : (
        <Flex gap={6} align="flex-start">
          <VStack gap={4} align="stretch" flex={1} minW="360px">
            {Object.keys(events)
              .filter((key) => {
                if (!query) return true;
                const v = (events[key].title || '') + ' ' + key;
                return v.toLowerCase().includes(query.toLowerCase());
              })
              .map((key) => (
                <Flex
                  key={key}
                  p={4}
                  borderRadius="md"
                  borderWidth={1}
                  align="center"
                >
                  <Box flex={1}>
                    <Text fontWeight="bold" fontSize="md">
                      {events[key].title || key}
                    </Text>
                    <Text fontSize="sm" color="gray.600" mt={2} noOfLines={2}>
                      {events[key].description || events[key].summary || '—'}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      ID: {key}
                    </Text>
                  </Box>
                  <HStack gap={2}>
                    <Button
                      as={RouterLink}
                      to={`/events/${key}`}
                      size="sm"
                      variant="outline"
                    >
                      View
                    </Button>
                    <Button
                      onClick={() => openEdit(key)}
                      size="sm"
                      variant="outline"
                    >
                      Edit
                    </Button>
                    <Button
                      colorPalette="red"
                      onClick={() => openDelete(key)}
                      size="sm"
                    >
                      Delete
                    </Button>
                  </HStack>
                </Flex>
              ))}
          </VStack>
        </Flex>
      )}

      <EventDialog
        isOpen={isDialogOpen}
        onClose={dialogOnClose}
        mode={dialogMode}
        selectedKey={selectedKey}
        initialValue={editorValue}
        isSaving={isSaving}
        onSave={handleDialogSave}
      />

      <Dialog.Root
        placement="center"
        motionPreset="slide-in-bottom"
        open={deleteIsOpen}
        onOpenChange={(details) => !details.open && deleteOnClose()}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>Confirm Deletion</Dialog.Title>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body>
                Are you sure you want to delete <strong>{selectedKey}</strong>?
                This action cannot be undone.
              </Dialog.Body>
              <Dialog.Footer>
                <Flex justify="flex-end" gap={2} w="full">
                  <Button onClick={deleteOnClose} variant="outline">
                    Cancel
                  </Button>
                  <Button
                    colorPalette="red"
                    onClick={deleteEvent}
                    loading={isSaving}
                  >
                    Delete
                  </Button>
                </Flex>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
}
